import { Component, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatDialog } from '@angular/material/dialog';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { FinanceLedgerService } from '../../../services/finance-ledger/finance-ledger.service';
import {
  BankReconciliationImport,
  BankReconciliationRow,
  BankReconciliationState,
} from '../../../services/ledger-client/types';
import { FINANCE_TABS, FinanceTab } from '../finance-tabs';
import { ConfirmDialogComponent, ConfirmDialogModel } from '../../modal/confirmdialog/confirmdialog.component';

type MappingKey = 'date' | 'description' | 'reference' | 'amount' | 'debit' | 'credit' | 'currency';
type RowSortField =
  | 'rowNumber'
  | 'transactionDate'
  | 'description'
  | 'bankReference'
  | 'direction'
  | 'amount'
  | 'state'
  | 'stateReason'
  | 'movementProvider'
  | 'movementExternalId'
  | 'movementGrossAmount'
  | 'movementTransactionAmount'
  | 'movementWallitFeeAmount'
  | 'movementFeeAmount'
  | 'movementNetAmount'
  | 'discrepancyAmount'
  | 'movementContractId'
  | 'dimensions'
  | 'matchedMovementId'
  | 'ledgerTransactionId';
type ExportFormat = 'csv' | 'tsv';

@Component({
    selector: 'app-bank-reconciliation',
    templateUrl: './bank-reconciliation.component.html',
    styleUrls: ['./bank-reconciliation.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class BankReconciliationComponent implements OnInit, OnDestroy {
  readonly tabs: FinanceTab[] = FINANCE_TABS;
  readonly activeTab = this.tabs.find(tab => tab.id === 'bank-reconciliation') || this.tabs[0];
  readonly states: Array<{value: '' | BankReconciliationState; label: string}> = [
    {value: '', label: 'All states'},
    {value: 'MATCHED', label: 'Matched'},
    {value: 'PARTIALLY_MATCHED', label: 'Partial'},
    {value: 'UNMATCHED', label: 'Unmatched'},
    {value: 'AMBIGUOUS', label: 'Ambiguous'},
    {value: 'FAILED', label: 'Failed'},
  ];
  readonly mappingFields: Array<{key: MappingKey; label: string; required: boolean}> = [
    {key: 'date', label: 'Date', required: true},
    {key: 'description', label: 'Description', required: true},
    {key: 'reference', label: 'Reference', required: false},
    {key: 'amount', label: 'Signed amount', required: false},
    {key: 'debit', label: 'Debit', required: false},
    {key: 'credit', label: 'Credit', required: false},
    {key: 'currency', label: 'Currency', required: false},
  ];

  imports: BankReconciliationImport[] = [];
  selectedImport?: BankReconciliationImport;
  rows: BankReconciliationRow[] = [];
  file?: File;
  headers: string[] = [];
  mapping: Partial<Record<MappingKey, string>> = {};
  accountLabel = '';
  accountCurrency = 'USD';
  stateFilter: '' | BankReconciliationState = '';
  rowSearch = '';
  rowSortBy: RowSortField = 'rowNumber';
  rowSortDirection: 'asc' | 'desc' = 'asc';
  importOffset = 0;
  readonly pageSize = 100;
  readonly exportPageSize = 500;
  loading = false;
  loadingRows = false;
  hasMoreRows = true;
  importing = false;
  exportingImportId?: string;
  deletingImportId?: string;
  private rowRequestId = 0;
  private searchTimer?: ReturnType<typeof setTimeout>;
  private readonly destroy$ = new Subject<void>();

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private snackBar: MatSnackBar,
    private financeLedgerService: FinanceLedgerService,
    private dialog: MatDialog,
  ) {}

  ngOnInit(): void {
    this.route.paramMap.pipe(takeUntil(this.destroy$)).subscribe(params => {
      const importId = params.get('importId');
      if (importId) {
        this.loadImport(importId);
      } else {
        this.selectedImport = undefined;
        this.loadImports();
      }
    });
  }

  ngOnDestroy(): void {
    if (this.searchTimer) {
      clearTimeout(this.searchTimer);
    }
    this.destroy$.next();
    this.destroy$.complete();
  }

  gotoTab(tab: FinanceTab): void {
    this.router.navigate([tab.link]).then();
  }

  selectFile(event: Event): void {
    const input = event.target as HTMLInputElement;
    const candidate = input.files && input.files[0];
    if (!candidate) {
      return;
    }
    const extension = candidate.name.toLowerCase().split('.').pop();
    if (extension !== 'csv' && extension !== 'tsv') {
      this.snackBar.open('Select a .csv or .tsv file.', 'Close', {duration: 4000});
      input.value = '';
      return;
    }
    if (candidate.size > 5 * 1024 * 1024) {
      this.snackBar.open('The CSV or TSV file must be 5 MB or smaller.', 'Close', {duration: 4000});
      input.value = '';
      return;
    }
    this.file = candidate;
    const reader = new FileReader();
    reader.onload = () => {
      this.headers = this.parseHeader(String(reader.result || ''), extension === 'csv' ? ',' : '\t');
      this.mapping = this.defaultMapping(this.headers);
      if (!this.headers.length) {
        this.snackBar.open('The selected file does not contain a header row.', 'Close', {duration: 4000});
      }
    };
    reader.onerror = () => this.snackBar.open('Unable to read the selected file.', 'Close', {duration: 4000});
    reader.readAsText(candidate);
  }

  importFile(): void {
    if (!this.file || !this.isMappingValid()) {
      this.snackBar.open('Map the required columns and either Amount or both Debit and Credit.', 'Close', {duration: 5000});
      return;
    }
    const formData = new FormData();
    formData.append('file', this.file, this.file.name);
    formData.append('bankName', 'Bangor Savings');
    if (this.accountLabel.trim()) {
      formData.append('accountLabel', this.accountLabel.trim());
    }
    formData.append('accountCurrency', this.accountCurrency.trim().toUpperCase());
    formData.append('columnMapping', JSON.stringify(this.compactMapping()));
    this.importing = true;
    this.financeLedgerService.createBankReconciliationImport(formData).then(result => {
      this.importing = false;
      this.snackBar.open(
        result.wasExisting ? 'This file was already processed. Opening the existing import.' : 'Bank reconciliation import completed.',
        'Close',
        {duration: 5000},
      );
      this.openImport(result.item);
    }).catch(error => {
      this.importing = false;
      this.snackBar.open(this.financeLedgerService.getErrorMessage(error, 'Unable to import bank file'), 'Close', {duration: 7000});
    });
  }

  isMappingValid(): boolean {
    const required = Boolean(this.mapping.date && this.mapping.description);
    const amountMode = Boolean(this.mapping.amount);
    const debitCreditMode = Boolean(this.mapping.debit && this.mapping.credit);
    return required && (amountMode || debitCreditMode)
      && /^[A-Za-z]{3}$/.test(this.accountCurrency.trim());
  }

  mappingValidationMessage(): string {
    const missing: string[] = [];
    if (!this.mapping.date) { missing.push('Date'); }
    if (!this.mapping.description) { missing.push('Description'); }
    if (!this.mapping.amount && !(this.mapping.debit && this.mapping.credit)) {
      missing.push('Signed amount or both Debit and Credit');
    }
    if (!/^[A-Za-z]{3}$/.test(this.accountCurrency.trim())) {
      missing.push('a three-letter account currency');
    }
    return missing.length ? `Required mapping: ${missing.join(', ')}.` : '';
  }

  loadImports(): void {
    this.loading = true;
    this.financeLedgerService.listBankReconciliationImports(this.pageSize, this.importOffset).then(items => {
      this.imports = items;
      this.loading = false;
    }).catch(error => this.handleLoadError(error, 'Unable to load bank reconciliation imports'));
  }

  openImport(item: BankReconciliationImport): void {
    this.router.navigate(['/finance/bank-reconciliation', item.id]).then();
  }

  backToImports(): void {
    this.router.navigate(['/finance/bank-reconciliation']).then();
  }

  changeState(): void {
    this.loadRows(true);
  }

  changeSearch(value: string): void {
    this.rowSearch = value;
    if (this.searchTimer) {
      clearTimeout(this.searchTimer);
    }
    this.searchTimer = setTimeout(() => this.loadRows(true), 300);
  }

  previousImports(): void {
    this.importOffset = Math.max(0, this.importOffset - this.pageSize);
    this.loadImports();
  }

  nextImports(): void {
    this.importOffset += this.pageSize;
    this.loadImports();
  }

  loadMoreRows(): void {
    this.loadRows();
  }

  sortRows(column: RowSortField): void {
    if (this.rowSortBy === column) {
      this.rowSortDirection = this.rowSortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.rowSortBy = column;
      this.rowSortDirection = 'asc';
    }
    this.loadRows(true);
  }

  sortIcon(column: RowSortField): string {
    if (this.rowSortBy !== column) { return 'unfold_more'; }
    return this.rowSortDirection === 'asc' ? 'arrow_upward' : 'arrow_downward';
  }

  sortAria(column: RowSortField): 'ascending' | 'descending' | null {
    if (this.rowSortBy !== column) { return null; }
    return this.rowSortDirection === 'asc' ? 'ascending' : 'descending';
  }

  formatCreatedBy(value: BankReconciliationImport['createdBy']): string {
    if (!value) { return '-'; }
    const name = [value.firstName, value.lastName].filter(Boolean).join(' ').trim();
    return [name, value.email].filter(Boolean).join(' · ') || '-';
  }

  batchDetails(row: BankReconciliationRow): string {
    const correlation = row.movementMetadata?.['stripeDwollaCorrelation'] as {
      batchId?: string; state?: string; stripePayoutId?: string; transferAmountCents?: number;
      contributions?: Array<{stripePayoutId: string; transferAmountCents: number}>;
    } | undefined;
    if (!correlation || (!correlation.batchId && correlation.state !== 'PENDING_BATCH')) { return ''; }
    const allocations = correlation.contributions || (correlation.stripePayoutId ? [{
      stripePayoutId: correlation.stripePayoutId, transferAmountCents: correlation.transferAmountCents,
    }] : []);
    const amounts = allocations.map(item => `${item.stripePayoutId}: $${(item.transferAmountCents! / 100).toFixed(2)}`).join('; ');
    return `${correlation.batchId ? 'Batch ' + correlation.batchId : 'Awaiting batch'} · ${correlation.state}${amounts ? ' · ' + amounts : ''}`;
  }

  dimensions(row: BankReconciliationRow): string {
    const dimensions = row.movementDimensions;
    if (!dimensions) { return '-'; }
    return [
      dimensions.teams.length && `Teams: ${dimensions.teams.join(', ')}`,
      dimensions.companies.length && `Companies: ${dimensions.companies.join(', ')}`,
      dimensions.users.length && `Users: ${dimensions.users.join(', ')}`,
      dimensions.products?.length && `Products/rewards: ${dimensions.products.join(', ')}`,
    ].filter(Boolean).join(' · ') || '-';
  }

  async exportImport(item: BankReconciliationImport, format: ExportFormat, currentView = false): Promise<void> {
    if (this.exportingImportId) { return; }
    this.exportingImportId = item.id;
    try {
      const rows: BankReconciliationRow[] = [];
      let page: BankReconciliationRow[];
      do {
        page = await this.financeLedgerService.listBankReconciliationRows(
          item.id,
          currentView && this.stateFilter ? this.stateFilter : undefined,
          this.exportPageSize,
          rows.length,
          currentView ? this.rowSearch : '',
          currentView ? this.rowSortBy : 'rowNumber',
          currentView ? this.rowSortDirection : 'asc',
        );
        rows.push(...page);
      } while (page.length === this.exportPageSize);

      const filenameBase = item.sourceFilename.replace(/\.(csv|tsv)$/i, '').replace(/[^a-zA-Z0-9._-]+/g, '-');
      this.downloadExport(
        `${filenameBase}-reconciliation.${format}`,
        this.serializeRows(rows, format),
        format,
      );
      this.snackBar.open(`Exported ${rows.length} reconciliation rows.`, 'Close', {duration: 4000});
    } catch (error) {
      this.snackBar.open(
        this.financeLedgerService.getErrorMessage(error, 'Unable to export reconciliation rows'),
        'Close',
        {duration: 7000},
      );
    } finally {
      this.exportingImportId = undefined;
    }
  }

  async removeImport(item: BankReconciliationImport): Promise<void> {
    if (this.deletingImportId) { return; }
    const dialogRef = this.dialog.open<ConfirmDialogComponent, ConfirmDialogModel, boolean>(ConfirmDialogComponent, {
      data: new ConfirmDialogModel(
        'Remove bank reconciliation import',
        `Remove <strong>${this.escapeHtml(item.sourceFilename)}</strong> and all of its reconciliation results? This cannot be undone.`,
        false,
        'Remove',
        'Cancel',
      ),
      autoFocus: false,
    });
    const confirmed = await dialogRef.afterClosed().toPromise();
    if (!confirmed) { return; }

    this.deletingImportId = item.id;
    try {
      await this.financeLedgerService.deleteBankReconciliationImport(item.id);
      if (this.selectedImport?.id === item.id) {
        await this.router.navigate(['/finance/bank-reconciliation']);
      } else {
        this.imports = this.imports.filter(existing => existing.id !== item.id);
      }
      this.snackBar.open('Bank reconciliation import removed.', 'Close', {duration: 4000});
    } catch (error) {
      this.snackBar.open(
        this.financeLedgerService.getErrorMessage(error, 'Unable to remove bank reconciliation import'),
        'Close',
        {duration: 7000},
      );
    } finally {
      this.deletingImportId = undefined;
    }
  }

  private loadImport(importId: string): void {
    this.loading = true;
    this.financeLedgerService.getBankReconciliationImport(importId).then(item => {
      this.selectedImport = item;
      this.loading = false;
      this.loadRows(true);
    }).catch(error => this.handleLoadError(error, 'Unable to load bank reconciliation import'));
  }

  loadRows(reset = false): void {
    if (!this.selectedImport) { return; }
    if (reset) {
      this.rowRequestId++;
      this.rows = [];
      this.hasMoreRows = true;
      this.loadingRows = false;
    }
    if (this.loadingRows || !this.hasMoreRows) { return; }

    const requestId = ++this.rowRequestId;
    const offset = this.rows.length;
    this.loadingRows = true;
    this.financeLedgerService.listBankReconciliationRows(
      this.selectedImport.id,
      this.stateFilter || undefined,
      this.pageSize,
      offset,
      this.rowSearch,
      this.rowSortBy,
      this.rowSortDirection,
    ).then(rows => {
      if (requestId !== this.rowRequestId) { return; }
      this.rows = [...this.rows, ...rows];
      this.hasMoreRows = rows.length === this.pageSize;
      this.loadingRows = false;
    }).catch(error => {
      if (requestId !== this.rowRequestId) { return; }
      this.loadingRows = false;
      this.snackBar.open(this.financeLedgerService.getErrorMessage(error, 'Unable to load reconciliation rows'), 'Close', {duration: 7000});
    });
  }

  private handleLoadError(error: unknown, message: string): void {
    this.loading = false;
    this.snackBar.open(this.financeLedgerService.getErrorMessage(error, message), 'Close', {duration: 7000});
  }

  private serializeRows(rows: BankReconciliationRow[], format: ExportFormat): string {
    const delimiter = format === 'csv' ? ',' : '\t';
    const columns: Array<{header: string; value: (row: BankReconciliationRow) => string | number}> = [
      {header: 'Date', value: row => row.transactionDate || ''},
      {header: 'Description', value: row => row.description || ''},
      {header: 'Bank reference', value: row => row.bankReference || ''},
      {header: 'Direction', value: row => row.direction || ''},
      {header: 'Bank amount', value: row => this.exportAmount(row.amount)},
      {header: 'State', value: row => row.state},
      {header: 'State reason', value: row => row.stateReason || ''},
      {header: 'Provider', value: row => row.movementProvider || ''},
      {header: 'Provider transaction / reference', value: row => row.movementExternalId || ''},
      {header: 'Transaction amount', value: row => this.exportAmount(row.movementTransactionAmount)},
      {header: 'Wallit fee', value: row => this.exportAmount(row.movementWallitFeeAmount)},
      {header: 'Provider fee', value: row => this.exportAmount(row.movementFeeAmount)},
      {header: 'Net', value: row => this.exportAmount(row.movementNetAmount)},
      {header: 'Discrepancy', value: row => this.exportAmount(row.discrepancyAmount)},
      {header: 'Contract', value: row => row.movementContractId || ''},
      {header: 'Dimensions', value: row => this.dimensions(row) === '-' ? '' : this.dimensions(row)},
      {header: 'Wallit movement ID', value: row => row.matchedMovementId || ''},
      {header: 'Journal transaction ID', value: row => row.ledgerTransactionId || ''},
      {header: 'Dwolla funding batch', value: row => this.batchDetails(row)},
      {header: 'Stripe Dwolla funding evidence', value: row => row.movementMetadata?.['stripeDwollaCorrelation'] ? JSON.stringify(row.movementMetadata['stripeDwollaCorrelation']) : ''},
    ];
    const records = [
      columns.map(column => column.header),
      ...rows.map(row => columns.map(column => column.value(row))),
    ];
    return records.map(record => record.map(value => this.escapeExportCell(value, delimiter)).join(delimiter)).join('\r\n');
  }

  private exportAmount(value: number | null | undefined): string {
    return value === null || value === undefined ? '' : Number(value).toFixed(2);
  }

  private escapeExportCell(value: string | number, delimiter: string): string {
    let text = String(value);
    const numericText = /^-?(?:\d+|\d*\.\d+)$/.test(text);
    if (typeof value === 'string' && (/^[=+@]/.test(text) || (text.startsWith('-') && !numericText))) {
      text = `'${text}`;
    }
    if (text.includes('"') || text.includes(delimiter) || /[\r\n]/.test(text)) {
      return `"${text.replace(/"/g, '""')}"`;
    }
    return text;
  }

  private escapeHtml(value: string): string {
    return value.replace(/[&<>"']/g, character => ({
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#39;',
    }[character] || character));
  }

  private downloadExport(filename: string, content: string, format: ExportFormat): void {
    const mimeType = format === 'csv' ? 'text/csv;charset=utf-8' : 'text/tab-separated-values;charset=utf-8';
    const url = URL.createObjectURL(new Blob([`\uFEFF${content}`], {type: mimeType}));
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = filename;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL(url);
  }

  private compactMapping(): Record<string, string> {
    const result: Record<string, string> = {};
    this.mappingFields.forEach(field => {
      const value = this.mapping[field.key];
      if (value) { result[field.key] = value; }
    });
    return result;
  }

  private defaultMapping(headers: string[]): Partial<Record<MappingKey, string>> {
    const result: Partial<Record<MappingKey, string>> = {
      date: this.findHeader(headers, ['Date', 'Transaction Date', 'Posted Date', 'Posting Date']),
      description: this.findHeader(headers, ['Transaction Description', 'Description', 'Memo', 'Details']),
      reference: this.findHeader(headers, [
        'Cust. Ref. / Check Number',
        'Customer Reference / Check Number',
        'Bank Reference',
        'Reference',
        'Check Number',
      ]),
      currency: this.findHeader(headers, ['Currency', 'Currency Code']),
    };
    const debit = this.findHeader(headers, ['Debit', 'Debit Amount', 'Withdrawal', 'Withdrawals']);
    const credit = this.findHeader(headers, ['Credit', 'Credit Amount', 'Deposit', 'Deposits']);
    if (debit && credit) {
      result.debit = debit;
      result.credit = credit;
    } else {
      result.amount = this.findHeader(headers, ['Amount', 'Signed Amount', 'Transaction Amount']);
    }
    return result;
  }

  private findHeader(headers: string[], aliases: string[]): string | undefined {
    const normalizedHeaders = new Map(
      headers.map(header => [header.trim().replace(/\s+/g, ' ').toLowerCase(), header]),
    );
    for (const alias of aliases) {
      const header = normalizedHeaders.get(alias.toLowerCase());
      if (header) { return header; }
    }
    return undefined;
  }

  private parseHeader(text: string, delimiter: ',' | '\t'): string[] {
    const firstLine = text.replace(/^\uFEFF/, '').split(/\r?\n/, 1)[0] || '';
    const values: string[] = [];
    let value = '';
    let quoted = false;
    for (let index = 0; index < firstLine.length; index++) {
      const char = firstLine[index];
      if (char === '"') {
        if (quoted && firstLine[index + 1] === '"') {
          value += '"';
          index++;
        } else {
          quoted = !quoted;
        }
      } else if (char === delimiter && !quoted) {
        values.push(value);
        value = '';
      } else {
        value += char;
      }
    }
    values.push(value);
    return values.map(header => header.trim()).filter(Boolean);
  }
}
